import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { EmptyList } from '@components/EmptyList';
import { Accordion } from '@components/ui/accordion';
import { queryKeys } from '@constants/queryKeys.constant';
import { taskTypesNames } from '@constants/tasks.constant';
import { EStaleTime } from '@enums/staleTime.enum';
import {
  getAllTasksSchema,
  type GetAllTasksParams,
} from '@services/tasks/dtos/getAll.dto';
import { taskService } from '@services/tasks/service';

import { TasksFilters } from './@components/Filters';
import { TaskItem } from './@components/Item';

function tasksQueryOptions(params: GetAllTasksParams) {
  return queryOptions({
    staleTime: EStaleTime.OneHour,
    queryKey: queryKeys.tasks.list(params),
    queryFn: () =>
      taskService.getAll({
        dueDateStatus: params.dueDateStatus,
        showAll: params.showAll,
      }),
  });
}

export const Route = createFileRoute('/_layout/(private)/tasks/')({
  component: Tasks,
  validateSearch: search => getAllTasksSchema.parse(search),
  loaderDeps: ({ search }) => ({
    dueDateStatus: search.dueDateStatus,
    showAll: search.showAll,
  }),
  loader: ({ context, deps }) => {
    const options = tasksQueryOptions(deps);
    return context.queryClient.ensureQueryData({
      ...options,
      revalidateIfStale: true,
    });
  },
});

function Tasks() {
  const { dueDateStatus, showAll } = Route.useSearch();

  const { data } = useSuspenseQuery(
    tasksQueryOptions({ dueDateStatus, showAll }),
  );

  return (
    <div className="relative">
      <TasksFilters
        dueToday={data.counts.dueToday}
        overdue={data.counts.overdue}
        upcoming={data.counts.upcoming}
      />
      <div
        className="bg-background mb-2 grid w-full overflow-hidden rounded-xl border border-zinc-200 shadow"
        role="table"
      >
        <div
          className="bg-primary-light-two dark:text-primary-dark-one flex min-h-14 items-center justify-end gap-x-2 px-8 font-medium text-zinc-600"
          role="row"
        >
          <span className="font-medium">Total tasks selected:</span>
          <b className="font-semibold">{data.counts.total || 0}</b>
        </div>

        <Accordion.Root className="grid gap-1" type="multiple">
          {data.list.map(task => (
            <Accordion.Item key={task.type} role="row" value={task.type}>
              <Accordion.Trigger className="min-h-12 w-full items-center bg-zinc-200/50 px-6 font-semibold transition-all duration-200">
                {taskTypesNames[task.type]} ({task.count})
              </Accordion.Trigger>
              <Accordion.Content className="px-4">
                {task.list.map(item => (
                  <TaskItem
                    businessLine={item.businessLine}
                    dueDate={item.dueDate}
                    key={item.id}
                    requester={item.requester}
                    serviceRequestId={item.serviceRequestId}
                  />
                ))}
              </Accordion.Content>
            </Accordion.Item>
          ))}

          <EmptyList
            alt="No tasks animation/image"
            animation="tasks"
            description="Create a service request and/or intervention task to see it here"
            isVisible={!data.list.length}
            title="No tasks yet."
          />
        </Accordion.Root>
      </div>
    </div>
  );
}
